"use client";

import Image from "next/image";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { secToHourMin } from "@/helpers/secToMinS";
import { toast } from "react-toastify";

function WorkshopCardContent({ workshop, freeReplayLabel }) {
    return (
        <>
            {/* Image */}
            <div className="relative w-full overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 aspect-[5/2]">
                {workshop.thumbnail ? (
                    <Image
                        src={workshop.thumbnail}
                        alt={workshop.title}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="object-cover group-hover:scale-105 transition-transform duration-300"
                        priority={false}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <SolarIcon name="videocamera-linear" className="h-16 w-16 text-primary/40" />
                    </div>
                )}
                {workshop.duration && (
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                        {secToHourMin(workshop.duration)}
                    </div>
                )}
            </div>

            <CardHeader className="pb-3 flex-1">
                <CardTitle className="leading-6 line-clamp-2">
                    <h2>{workshop.title}</h2>
                </CardTitle>
                <CardDescription className="line-clamp-3 mt-2">
                    {workshop.description}
                </CardDescription>
            </CardHeader>

            <CardContent className="px-6 pb-4">
                <div className="flex flex-wrap gap-2">
                    {/* Tag accès gratuit si public */}
                    {workshop.isPublic && (
                        <Badge className="text-xs font-medium bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 border border-emerald-500/20 transition-colors duration-200">
                            <SolarIcon name="play-bold" className="h-3 w-3 mr-1.5 flex-shrink-0" />
                            <span>{freeReplayLabel}</span>
                        </Badge>
                    )}
                    {/* Tags des accélérateurs */}
                    {workshop.accelerators && workshop.accelerators.length > 0 &&
                        workshop.accelerators.map((accelerator) => (
                            <Badge
                                key={accelerator.id}
                                variant="secondary"
                                className="text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-colors duration-200"
                            >
                                <SolarIcon name="users-group-rounded-linear" className="h-3 w-3 mr-1.5 flex-shrink-0" />
                                <span className="truncate">
                                    {accelerator.title}
                                </span>
                            </Badge>
                        ))}
                </div>
            </CardContent>
        </>
    );
}

export default function WorkshopCard({ workshop, message, freeReplayLabel }) {
    const handleClick = () => {
        toast.success(message);
    };

    if (workshop.isPublic) {
        return (
            <Link
                href={`/workshops/${workshop.slug}/play`}
                target="_blank"
                className="block h-full"
            >
                <Card className="hover:bg-gray-50 dark:hover:bg-grayDark-800 transition-all duration-100 h-full flex flex-col group overflow-hidden cursor-pointer">
                    <WorkshopCardContent workshop={workshop} freeReplayLabel={freeReplayLabel} />
                </Card>
            </Link>
        );
    }

    return (
        <Card
            className="hover:bg-gray-50 dark:hover:bg-grayDark-800 transition-all duration-100 h-full flex flex-col group overflow-hidden cursor-pointer"
            onClick={handleClick}
        >
            <WorkshopCardContent workshop={workshop} />
        </Card>
    );
}
